import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { TranslocoService } from '@jsverse/transloco';

import { Subscription } from 'rxjs';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Group, GroupService } from '../../../../providers/semantria-api/group.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from '../../../../providers/semantria-api/semantria-users.service';
import { AlertService } from '../../../../services/alert.service';

export interface UserAddDialogOptions {
  group: Group;
  allUsers: SemantriaUser[];
}

interface NewGroupPermission {
  newGroupPermissions: string[];
}

export enum Permission {
  ReadOnly = 'READ_ONLY',
  AllAccess = 'ALL_ACCESS',
  ExecutiveReadOnly = 'EXECUTIVE_READ_ONLY',
}

@EnableClassTelemetry()
@Component({
  selector: 'app-user-add-dialog',
  templateUrl: './user-add-dialog.component.html',
  styleUrls: ['./user-add-dialog.component.scss'],
  standalone: false,
})
export class UserAddDialogComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: UserAddDialogOptions;
  @Output() modalClosed: EventEmitter<SemantriaUser[]> = new EventEmitter<SemantriaUser[]>();
  private translationSubscription: Subscription;

  @ViewChild(MatSort, { static: false }) set content(sort: MatSort) {
    this.allUsersDs.sort = sort;
    this.allUsersDs.sortData = this.sortData;
  }
  @ViewChild(MatPaginator, { static: false }) set paginator(v: MatPaginator) {
    this.allUsersDs.paginator = v;
  }

  displayedColumns = ['add', 'email', 'name'];
  permissionRadios = [
    {
      value: Permission.ReadOnly,
      displayName: 'Read Only',
    },
    {
      value: Permission.AllAccess,
      displayName: 'All Access',
    },
    {
      value: Permission.ExecutiveReadOnly,
      displayName: 'Executive View',
    },
  ];

  allUsersDs: MatTableDataSource<SemantriaUser>;
  selectedUsers: (SemantriaUser & NewGroupPermission)[] = [];
  selectedPermission = Permission.ReadOnly;

  isSaving: boolean;
  currentStep: 1 | 2 = 1;

  allComplete = false;
  someComplete = false;

  constructor(
    private groupService: GroupService,
    private userService: SemantriaUsersService,
    private alertService: AlertService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.translationSubscription = this.translocoService
      .selectTranslateObject('user-add-dialog.columns')
      .subscribe((translatedValue) => {
        this.permissionRadios.forEach(function (value) {
          switch (value.value) {
            case Permission.AllAccess:
              value.displayName = translatedValue['all-access'];
              break;
            case Permission.ExecutiveReadOnly:
              value.displayName = translatedValue['executive-view'];
              break;
            case Permission.ReadOnly:
              value.displayName = translatedValue['read-only'];
              break;
          }
        });
      });
    this.allUsersDs = new MatTableDataSource<SemantriaUser>([
      { email: 'Select All' } as any,
      ...this.data.allUsers,
    ]);
  }

  ngOnDestroy() {
    this.translationSubscription?.unsubscribe();
  }

  sortData(data: SemantriaUser[], sort: MatSort): SemantriaUser[] {
    const direction = sort.direction === 'asc' ? 1 : -1;
    const selectAll = data.shift();
    const newData = data.sort((u1, u2) => {
      switch (sort.active) {
        case 'name':
          let ret = u1.first_name.localeCompare(u2.first_name);
          if (!ret) {
            ret = u1.last_name.localeCompare(u2.last_name);
          }
          return ret * direction;
        case 'email':
          return u1.email.localeCompare(u2.email) * direction;
        default:
          return 0;
      }
    });

    newData.unshift(selectAll);
    return newData;
  }

  public isUserSelected(user: SemantriaUser & NewGroupPermission): boolean {
    return this.selectedUsers.includes(user);
  }

  toggleUser(user: SemantriaUser & NewGroupPermission) {
    const idx = this.selectedUsers.indexOf(user);
    if (idx === -1) {
      if (this.selectedPermission === Permission.ExecutiveReadOnly) {
        user.newGroupPermissions = [Permission.ReadOnly, Permission.ExecutiveReadOnly];
      } else {
        user.newGroupPermissions = [this.selectedPermission];
      }

      this.selectedUsers.push(user);
    } else {
      this.selectedUsers.splice(idx, 1);
      delete user.newGroupPermissions;
    }

    this.updateAllComplete();
  }

  canAddUsers() {
    return (
      !this.isSaving &&
      this.selectedUsers.length &&
      this.selectedUsers.every((u) => u.newGroupPermissions)
    );
  }

  cancel() {
    this.modalClosed.emit(null);
  }

  async add() {
    if (this.selectedUsers.length) {
      this.isSaving = true;
      try {
        await this.groupService.addGroupMembers(
          this.data.group.group_id,
          this.selectedUsers.map((user) => user.user_id)
        );
        // Need to update this to add the group to the user object.
        this.selectedUsers.forEach((user) => {
          this.data.allUsers.forEach((parentUser) => {
            if (parentUser.user_id === user.user_id) {
              user.user_groups.push(this.data.group.group_id);
            }
          });
        });
      } catch (err) {
        this.isSaving = false;

        this.alertService.showToast(
          this.selectedUsers.length > 1
            ? this.translocoService.translate('user-add-dialog.error.failed-to-add-user-plural')
            : this.translocoService.translate('user-add-dialog.error.failed-to-add-user-singular'),
          err
        );
        // Remove newGroupPermissions property before emitting
        const selectedUsers: SemantriaUser[] = this.selectedUsers.map(
          ({ ...otherProps }) => otherProps
        );
        this.modalClosed.emit(selectedUsers);
      }

      for (const user of this.selectedUsers.filter(
        (u) => !u.newGroupPermissions.every((p) => p === Permission.AllAccess)
      )) {
        try {
          const currentPermissions = SemantriaUsersService.getGroupPermissions(
            user,
            this.data.group.group_id
          );

          for (const newPermission of user.newGroupPermissions) {
            if (currentPermissions.some((p) => p.permission_type === newPermission)) {
              continue;
            }

            const permission = await this.userService.addPermission(
              user.user_id,
              newPermission,
              this.data.group.group_id
            );
            const permissonToSave = {
              permission_id: permission.permission_id,
              user_id: user.user_id,
              group_id: this.data.group.group_id,
              permission_type: permission.permission_type,
            };
            // Need to update this to update the display without selecting another group
            this.data.allUsers.forEach((parentUser) => {
              if (parentUser.user_id === user.user_id) {
                parentUser.permissions.push(permissonToSave);
              }
            });
          }
        } catch (err) {
          this.alertService.showErrorToast(
            this.translocoService.translate('user-add-dialog.error.failed-to-set-read-only'),
            err
          );
        }
      }

      this.alertService.showToast(
        this.selectedUsers.length > 1
          ? this.translocoService.translate('user-add-dialog.added-user-plural')
          : this.translocoService.translate('user-add-dialog.added-user-singular')
      );
      // Remove newGroupPermissions property before emitting
      const selectedUsers = this.selectedUsers.map(({ ...otherProps }) => otherProps);
      this.modalClosed.emit(selectedUsers);
    }
  }

  public back() {
    this.currentStep = 1;
    this.toggleAllUsers(true);
  }

  public getDisplayName(permission: Permission): string {
    return this.permissionRadios.find((radio) => radio.value === permission).displayName;
  }

  public updateAllComplete() {
    this.allComplete = this.selectedUsers.length === this.allUsersDs.data.length - 1;
    this.someComplete = !this.allComplete && this.selectedUsers.length !== 0;
  }

  public toggleAllUsers(deselectAll?: boolean): void {
    const deselectItems = deselectAll ?? this.allComplete;
    for (const _user of this.allUsersDs.data) {
      const user = <SemantriaUser & NewGroupPermission>_user;
      if (user.email !== 'Select All' && this.isUserSelected(user) === deselectItems) {
        this.toggleUser(user);
      }
    }
  }
}
